import {PxTrendMetaData} from '@domain/shared'

export type KpiDashboardPlant = {
  plantId: string
  name: string
  lastUpdate?: string
  parentLocationId?: string
  parentLocationType: KpiDashboardLocationType
  timezone?: string
  countryCode: string
  countryName: string
  pxTrendMetaData?: PxTrendMetaData
}

/**
 * @description Date format agreement with BE is 'YYYY-MM-DD'
 * @example '2021-01-31'
 */
type PerformanceDateRangeBounds = {
  startDate: string
  endDate: string
}

type DateRangeWithOffset = {
  dateRange: PerformanceDateRangeBounds
  yearOffset?: number
}

export type KpiDashboardLocationType = 'area' | 'region' | 'country' | 'plant'

export type KpiDashboardLocationData = {
  id: string
  name: string
  type?: KpiDashboardLocationType
  countryCode?: string
  path?: string
}

export enum KpisList {
  ClinkerProduction = 'clinkerProduction',
  CementProduction = 'cementProduction',
  KilnReliabilityCoefficient = 'kilnReliabilityCoefficient',
  KilnOperatingCoefficient = 'kilnOperatingCoefficient',
  KilnHeatConsumption = 'kilnHeatConsumption',
  AlternativeFuelRate = 'alternativeFuelRate',
  MTBF = 'meanTimeBetweenFailure',
  TechnicalCementPowerCons = 'technicalCementPowerConsumption'
}

type ActualProductionValue = {
  plannedCementProduction: number | null
  plannedClinkerProduction: number | null
  actualCementProduction: number
  actualClinkerProduction: number
  cementDelta: number
  clinkerDelta: number
  plantId: string
  unit: string
}

export type PlantStatusResponse = {
  plant: KpiDashboardPlant
  totalCement: number
  totalClinker: number
  unitCement: string
  unitClinker: string
  plannedYearlyProductionVolume: PlannedYearlyProductionVolumeResponse
  actualProductionValue?: ActualProductionValue
}

export type PlantStatus = {
  plant: KpiDashboardPlant
  plannedCementProduction: number
  plannedClinkerProduction: number
  actualCementProduction?: number
  actualClinkerProduction?: number
  totalCement: number
  totalClinker: number
  clinkerDelta?: number
  cementDelta?: number
  unitCement: string
  unitClinker: string
  plannedYearlyProductionVolume: PlannedYearlyProductionVolumeResponse
}

export type ActualAndPlannedData = {
  unit: string
  actual?: number
  planned: number
  delta?: number
}

export interface KPIs {
  alternativeFuelRate: ActualAndPlannedData
  kilnHeatConsumption: ActualAndPlannedData
  kilnReliabilityCoefficient: ActualAndPlannedData
  meanTimeBetweenFailure: ActualAndPlannedData
  kilnOperatingCoefficient: ActualAndPlannedData
  technicalCementPowerConsumption: ActualAndPlannedData
}

export interface KPIsWithProduction extends KPIs {
  clinkerProduction: ActualAndPlannedData
  cementProduction: ActualAndPlannedData
}

export type PlantKPIs = Partial<
  Record<
    keyof KPIs,
    {
      data: ActualAndPlannedData | undefined
      isError: boolean
    }
  >
>

export type ProductionDataType = {
  unit: string
  planned: number
  actual: number | undefined
  delta?: number
}

/** ***** HISTORICAL DATA TYPES ********* */

export type HistoricalData = {
  yearOffset: number
}

export type ProductionHistoricalData = HistoricalData & {
  actualCementProduction: number
  actualClinkerProduction: number
}

export type HistoricalKilnCoefficientData = HistoricalData & {
  actualOperatingCoefficient: number
  actualReliabilityCoefficient: number
}

export type HistoricalTechnicalCementPowerConsumptions = HistoricalData & {
  plannedTechnicalCementPowerConsumptions: number
  actualTechnicalCementPowerConsumptions: number
}

export type HistoricalKilnHeatConsumptions = HistoricalData & {
  plannedKilnHeatConsumptions: number
  actualKilnHeatConsumptions: number
}

export type HistoricalMeanTimeBetweenFailures = HistoricalData & {
  actualMeanTimeBetweenFailures: number
  plannedMeanTimeBetweenFailures: number
}

export type HistoricalAlternativeFuelRates = HistoricalData & {
  actualAlternativeFuelRates: number
  plannedAlternativeFuelRates: number
}

/** ***** RESPONSE TYPES ********* */

export type ProductionPerformanceValuesResponse = {
  plannedCementProduction: number
  actualCementProduction: number
  plannedClinkerProduction: number
  actualClinkerProduction: number
  clinkerDelta: number
  cementDelta: number
  historicalData: ProductionHistoricalData[]
  descendants: ProductionPerformanceValuesDescendantResponse[]
}

type PlannedYearlyProductionVolumeResponse = {
  plannedCementProduction: number
  plannedClinkerProduction: number
  year: number
  unit: string
  plantId: string
}

export type ProductionPerformanceValuesDescendantResponse = Omit<
  ProductionPerformanceValuesResponse,
  'locationData'
> & {
  locationData: KpiDashboardLocationData
  plannedYearlyProductionVolume: PlannedYearlyProductionVolumeResponse
}

export type QuarterlyForecastClinkerProductionHierarchy = {
  locationId: string
  quarterlyForecastClinkerProduction?: number
  delta?: number
  descendants: QuarterlyForecastClinkerProductionHierarchy[]
}

export type QuarterlyForecastCementProductionHierarchy = {
  locationId: string
  quarterlyForecastCementProduction?: number
  delta?: number
  descendants: QuarterlyForecastCementProductionHierarchy[]
}

export type QuarterlyForecastKilnCoefficientHierarchy = {
  locationId: string
  quarterlyForecastOperatingCoefficient?: number
  delta?: number
  descendants: QuarterlyForecastKilnCoefficientHierarchy[]
}

export type KilnCoefficientsResponse = {
  actualOperatingCoefficient: number
  actualReliabilityCoefficient: number
  plannedOperatingCoefficient: number
  plannedReliabilityCoefficient: number
  descendants: KilnCoefficientsResponse[]
  operatingDelta?: number
  reliabilityDelta?: number
  historicalData: HistoricalKilnCoefficientData[]
  location: KpiDashboardLocationData
  plantId?: string
}

export type PerformanceDescendantWithId<T> = T & {
  id: string
  locationData?: KpiDashboardLocationData
  location?: KpiDashboardLocationData
  subItems: PerformanceDescendantWithId<T>[]
  unit: string
}

export type QuarterlyForecastAlternativeFuelRatesHierarchy = {
  locationId: string
  quarterlyForecastAlternativeFuelRates?: number
  delta?: number
  descendants: QuarterlyForecastAlternativeFuelRatesHierarchy[]
}

export type QuarterlyForecastKilnHeatConsumptionsHierarchy = {
  locationId: string
  quarterlyForecastKilnHeatConsumptions?: number
  delta?: number
  descendants: QuarterlyForecastKilnHeatConsumptionsHierarchy[]
}

export type QuarterlyForecastTechnicalCementPowerConsumptionsHierarchy = {
  locationId: string
  quarterlyForecastTechnicalCementPowerConsumptions?: number
  delta?: number
  descendants: QuarterlyForecastTechnicalCementPowerConsumptionsHierarchy[]
}

export type TechnicalCementPowerConsumptionResponse = {
  actualTechnicalCementPowerConsumptions: number
  plannedTechnicalCementPowerConsumptions: number
  descendants: TechnicalCementPowerConsumptionResponse[]
  delta?: number
  historicalData: HistoricalTechnicalCementPowerConsumptions[]
  location: KpiDashboardLocationData
  plantId?: string
}

export type KilnHeatConsumptionsResponse = {
  actualKilnHeatConsumptions: number
  plannedKilnHeatConsumptions: number
  descendants: KilnHeatConsumptionsResponse[]
  delta?: number
  historicalData: HistoricalKilnHeatConsumptions[]
  location: KpiDashboardLocationData
  plantId?: string
}

export type MeanTimeBetweenFailuresResponse = {
  actualMeanTimeBetweenFailures: number
  plannedMeanTimeBetweenFailures: number
  descendants: MeanTimeBetweenFailuresResponse[]
  delta?: number
  historicalData: HistoricalMeanTimeBetweenFailures[]
  location: KpiDashboardLocationData
  plantId?: string
}

export type AlternativeFuelRates = {
  plantId: string
  actualAlternativeFuelRates: number
  plannedAlternativeFuelRates: number
  delta?: number
  unit: string
  locationData: KpiDashboardLocationData
  descendants: PerformanceDescendantWithId<AlternativeFuelRates>[]
  historicalData: HistoricalAlternativeFuelRates[]
  plannedYearlyProductionVolume?: number
}

export type AlternativeFuelRatesResponse = Omit<
  AlternativeFuelRates,
  'unit' | 'plannedYearlyProductionVolume' | 'locationData'
> & {
  descendants: AlternativeFuelRatesResponse[]
  location: KpiDashboardLocationData
}

export type PlannedAlternativeFuelRatesHierarchy = {
  plantId: string
  descendants: PlannedAlternativeFuelRatesHierarchy[]
  location: KpiDashboardLocationData
  plannedAlternativeFuelRates: number
}

export type PlannedYearlyKilnHeatConsumptionsHierarchy = {
  plantId: string
  descendants: PlannedYearlyKilnHeatConsumptionsHierarchy[]
  location: KpiDashboardLocationData
  plannedKilnHeatConsumptions: number
}

export type PlannedYearlyMeanTimeBetweenFailuresHierarchy = {
  plantId: string
  descendants: PlannedYearlyMeanTimeBetweenFailuresHierarchy[]
  location: KpiDashboardLocationData
  plannedMeanTimeBetweenFailures: number
}

export type PlannedYearlyTechnicalCementPowerConsumptionsHierarchy = {
  plantId: string
  descendants: PlannedYearlyTechnicalCementPowerConsumptionsHierarchy[]
  location: KpiDashboardLocationData
  plannedTechnicalCementPowerConsumptions: number
}

export type PlannedYearlyKilnCoefficientHierarchy = {
  plantId: string
  descendants: PlannedYearlyKilnCoefficientHierarchy[]
  location: KpiDashboardLocationData
  plannedOperatingCoefficient: number
  plannedReliabilityCoefficient: number
}

export type KpiPfyPayload = {
  locationId?: string
  year?: number
}

interface CommonKpiFetchParams {
  timezone: string
  locationId?: string
  showYTM?: boolean
}

/**
 * @description MTM and YTM are made for KPIs on location and plant performance pages
 * timestamp is used for mtm and ytm endpoints
 */
export interface MtmYtmRequestPayload extends CommonKpiFetchParams {
  timestamp: string
  year?: number
}

/**
 * @description Production performance values (clinker/cement) are fetched on location pages
 * DateRange is used BE endpoints which is generated(in DATE_ONLY_REQUEST_FORMAT) from TimeRange
 */
export interface ProductionRequestPayload extends CommonKpiFetchParams {
  yearOffset?: number // offset for the past data to be fetched
  timeRange: PerformanceDateRangeBounds
}

export type ProductionPerformanceValuesRequestPayload = DateRangeWithOffset
